// frontend/src/site/models/imobiliaria/SiteHomeImobiliaria.jsx
import { useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useSite } from '../../SiteContext';
import useSiteCatalog from '../../hooks/useSiteCatalog';

import SiteFilterBar from '../../components/SiteFilterBar';
import SiteListingGrid from '../../components/SiteListingGrid';

import { FaInstagram, FaFacebookF, FaWhatsapp } from 'react-icons/fa';

const LIMIT = 6;

function safeStr(v) {
  return String(v || '').toLowerCase();
}

function isTerreno(item) {
  const tipo = safeStr(item?.tipo || item?.categoria || item?.tipoImovel);
  return tipo.includes('terreno') || tipo.includes('lote');
}

function toInt(v, fallback) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function clampMax6(n) {
  const v = toInt(n, LIMIT);
  if (v <= 0) return LIMIT;
  return Math.min(LIMIT, v);
}

function pickSectionConfig(conteudo) {
  // ✅ aceita: conteudo.destaques (modelo imobiliária)
  // ✅ fallback: conteudo.imoveis (modelo corretor / legado)
  const c = conteudo && typeof conteudo === 'object' ? conteudo : {};
  return {
    destaques: c.destaques || c.imoveis || {},
    terrenos: c.terrenos || {},
  };
}

function pickButtonState(sec) {
  const show = sec?.mostrarBotaoVerTodos !== false && sec?.showButton !== false; // compat

  const label = sec?.textoBotaoVerTodos || sec?.buttonLabel || 'Ver todos';

  const action = sec?.buttonAction || 'imoveis';
  const url = sec?.buttonUrl || '';

  return { show, label, action, url };
}

export default function SiteHomeImobiliaria() {
  const { handle } = useParams();
  const navigate = useNavigate();

  const { corretorId, perfilSite, ownerWhatsUrl, conteudo } = useSite();

  // ✅ lê config do builder
  const { destaques: cfgDestaques, terrenos: cfgTerrenos } = useMemo(
    () => pickSectionConfig(conteudo),
    [conteudo]
  );

  // Quantidade de cards (máx 6)
  const qtdDestaques = clampMax6(
    cfgDestaques?.quantidade ?? cfgDestaques?.maxItems ?? 6
  );
  const qtdTerrenos = clampMax6(cfgTerrenos?.quantidade ?? 6);

  // Títulos/Subtítulos (builder > fallback)
  const tituloDestaques = cfgDestaques?.titulo || 'Imóveis em destaque';
  const subtituloDestaques =
    cfgDestaques?.subtitulo || 'Seleção de oportunidades.';

  const tituloTerrenos = cfgTerrenos?.titulo || 'Terrenos';
  const subtituloTerrenos =
    cfgTerrenos?.subtitulo || 'Lotes e oportunidades de terreno.';

  // Botões (builder)
  const btnDestaques = useMemo(
    () => pickButtonState(cfgDestaques),
    [cfgDestaques]
  );
  const btnTerrenos = useMemo(
    () => pickButtonState(cfgTerrenos),
    [cfgTerrenos]
  );

  const cover =
    perfilSite?.siteCoverUrl ||
    'data:image/svg+xml;utf8,' +
      encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" width="1600" height="900">
           <defs><linearGradient id="g" x1="0" x2="1">
             <stop stop-color="#0b1220"/><stop offset="1" stop-color="#111827"/>
           </linearGradient></defs>
           <rect width="100%" height="100%" fill="url(#g)"/>
         </svg>`
      );

  const logo =
    perfilSite?.logoUrl ||
    perfilSite?.logo ||
    perfilSite?.perfil?.logoUrl ||
    '';

  const ig = perfilSite?.instagramUrl || perfilSite?.perfil?.instagramUrl || '';
  const fb = perfilSite?.facebookUrl || perfilSite?.perfil?.facebookUrl || '';
  const whatsDisplay =
    perfilSite?.whatsappDisplay || perfilSite?.perfil?.whatsappDisplay || '';

  // ✅ 1) Query de DESTAQUES (limit 6)
  const {
    docs: destaqueDocsRaw,
    load: loadDestaques,
    loading: loadingDestaques,
  } = useSiteCatalog(corretorId, { publicado: true, destaque: true }, LIMIT);

  // ✅ 2) Query de RECENTES (para fallback + terrenos)
  const {
    docs: recentesDocsRaw,
    load: loadRecentes,
    loading: loadingRecentes,
  } = useSiteCatalog(corretorId, { publicado: true }, 24);

  useEffect(() => {
    if (!corretorId) return;

    loadDestaques();
    loadRecentes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corretorId]);

  const { imoveisParaMostrar, terrenosParaMostrar } = useMemo(() => {
    const recentes = Array.isArray(recentesDocsRaw) ? recentesDocsRaw : [];
    const destaques = Array.isArray(destaqueDocsRaw) ? destaqueDocsRaw : [];

    // Terrenos: sempre últimos N terrenos (dos recentes)
    const terrenos = recentes.filter(isTerreno).slice(0, qtdTerrenos);

    // Destaques: SOMENTE imóveis (não-terrenos) marcados como destaque
    const destaquesImoveis = destaques
      .filter((x) => !isTerreno(x))
      .slice(0, qtdDestaques);

    // Fallback: se não tiver nenhum destaque, pega últimas N casas (não-terrenos) dos recentes
    const recentesImoveis = recentes
      .filter((x) => !isTerreno(x))
      .slice(0, qtdDestaques);

    return {
      imoveisParaMostrar: destaquesImoveis.length
        ? destaquesImoveis
        : recentesImoveis,
      terrenosParaMostrar: terrenos,
    };
  }, [recentesDocsRaw, destaqueDocsRaw, qtdDestaques, qtdTerrenos]);

  function goResultados() {
    navigate(`/u/${handle}/imoveis`);
  }

  function handleButtonClick(btn, fallbackUrl) {
    if (!btn?.show) return;

    if (btn.action === 'url' && btn.url) {
      window.open(btn.url, '_blank', 'noopener,noreferrer');
      return;
    }

    navigate(fallbackUrl);
  }

  const loadingGrid = loadingDestaques || loadingRecentes;

  return (
    <div className="min-h-screen bg-[color:var(--bg)] text-[color:var(--text)]">
      {/* HERO fullscreen */}
      <header className="relative">
        <div className="relative w-full min-h-[100svh] md:min-h-screen">
          <img
            src={cover}
            alt="Capa do site"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/10 to-black/35" />

          {/* Topbar */}
          <div className="absolute inset-x-0 top-0 z-20">
            <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
              {/* Logo / Nome */}
              <div className="flex items-center gap-3">
                {logo ? (
                  <img
                    src={logo}
                    alt="Logo"
                    className="h-10 w-auto object-contain"
                  />
                ) : (
                  <div className="text-white font-semibold tracking-wide">
                    {perfilSite?.nome || 'Imobiliária'}
                  </div>
                )}
              </div>

              {/* Menu (âncoras simples) */}
              <nav className="hidden md:flex items-center gap-6 text-white/90 text-sm">
                <a href="#home" className="hover:text-white">
                  Home
                </a>
                <a href="#destaques" className="hover:text-white">
                  Imóveis
                </a>
                <a href="#terrenos" className="hover:text-white">
                  Terrenos
                </a>
                <a href="#contato" className="hover:text-white">
                  Contato
                </a>
              </nav>

              {/* Ícones redes */}
              <div className="flex items-center gap-3">
                {!!ownerWhatsUrl && (
                  <a
                    href={ownerWhatsUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="p-2 rounded-full bg-white/10 hover:bg-white/15 text-white"
                    aria-label="WhatsApp"
                    title={whatsDisplay || 'WhatsApp'}
                  >
                    <FaWhatsapp />
                  </a>
                )}
                {!!ig && (
                  <a
                    href={ig}
                    target="_blank"
                    rel="noreferrer"
                    className="p-2 rounded-full bg-white/10 hover:bg-white/15 text-white"
                    aria-label="Instagram"
                  >
                    <FaInstagram />
                  </a>
                )}
                {!!fb && (
                  <a
                    href={fb}
                    target="_blank"
                    rel="noreferrer"
                    className="p-2 rounded-full bg-white/10 hover:bg-white/15 text-white"
                    aria-label="Facebook"
                  >
                    <FaFacebookF />
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* FilterBar na base */}
          <div id="home" className="absolute inset-x-0 bottom-6 z-20">
            <div className="max-w-7xl mx-auto px-6">
              <SiteFilterBar />
              <div className="mt-4 flex justify-center">
                <button
                  type="button"
                  onClick={goResultados}
                  className="px-5 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white border border-white/20"
                >
                  Buscar
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Destaques (destaque -> fallback últimas casas) */}
      <section id="destaques" className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="text-2xl md:text-3xl font-semibold">
              {tituloDestaques}
            </h2>
            <p className="text-[color:var(--muted)] mt-2">
              {subtituloDestaques}
            </p>
          </div>

          {btnDestaques.show ? (
            <button
              type="button"
              onClick={() =>
                handleButtonClick(btnDestaques, `/u/${handle}/imoveis`)
              }
              className="px-4 py-2 rounded-xl border border-[color:var(--border)] hover:opacity-90"
            >
              {btnDestaques.label}
            </button>
          ) : null}
        </div>

        <div className="mt-8">
          <SiteListingGrid
            items={imoveisParaMostrar}
            loading={loadingGrid}
            exhausted={true}
            whatsappUrl={ownerWhatsUrl || ''}
            cols={{ base: 1, sm: 2, lg: 3, xl: 4 }}
          />
        </div>
      </section>

      {/* Terrenos (sempre últimos 6 terrenos) */}
      <section id="terrenos" className="max-w-7xl mx-auto px-6 pb-16">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="text-2xl md:text-3xl font-semibold">
              {tituloTerrenos}
            </h2>
            <p className="text-[color:var(--muted)] mt-2">
              {subtituloTerrenos}
            </p>
          </div>

          {btnTerrenos.show ? (
            <button
              type="button"
              onClick={() =>
                handleButtonClick(
                  btnTerrenos,
                  `/u/${handle}/imoveis?tipo=terreno`
                )
              }
              className="px-4 py-2 rounded-xl border border-[color:var(--border)] hover:opacity-90"
            >
              {btnTerrenos.label || 'Ver todos os terrenos'}
            </button>
          ) : null}
        </div>

        <div className="mt-8">
          <SiteListingGrid
            items={terrenosParaMostrar}
            loading={loadingGrid}
            exhausted={true}
            whatsappUrl={ownerWhatsUrl || ''}
            cols={{ base: 1, sm: 2, lg: 3, xl: 4 }}
          />
        </div>
      </section>

      {/* Rodapé simples */}
      <footer id="contato" className="border-t border-[color:var(--border)]">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row gap-6 md:items-center md:justify-between">
          <div className="text-sm text-[color:var(--muted)]">
            {perfilSite?.nome || 'Imobiliária'} © {new Date().getFullYear()}.
            Todos os direitos reservados.
          </div>

          <div className="flex items-center gap-3">
            {!!ownerWhatsUrl && (
              <a
                href={ownerWhatsUrl}
                target="_blank"
                rel="noreferrer"
                className="px-4 py-2 rounded-xl bg-[color:var(--accent)] text-[color:var(--accentText)]"
              >
                Fale conosco
              </a>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
}
